import {isBefore, startOfToday} from 'date-fns';
import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  closeDialog,
  DialogFooter,
  EmptyStatus,
  Form,
  FormButton,
  FormField,
  getHoursAndMinutesFromTime,
  Label,
  openConfirmDialog,
  openDialog,
  Separator,
  TimeType,
} from 'platform/components';
import {Box, Heading, HStack, VStack, Text, Space, Show, Grid} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {useMemo, useState} from 'react';
import {UseFormReturn} from 'react-hook-form';

import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetCurrentUserInfoQuery, useGetUserQuery, useGetUsersQuery} from '@dms/api/customer';
import {
  TestDriveResponseBody,
  useDeleteTestDriveMutation,
  useGetVehicleMileageQuery,
  useGetVehicleV2Query,
} from '@dms/api/sales';
import {GetVehicleApiResponse} from '@dms/api/vehicle';
import {mapUsersToSelectOptions, moveToHeadById} from '@dms/features/tasks';
import i18n from '@dms/i18n';
import {
  getNaturalPersonFullName,
  MileageInput,
  handleApiError,
  PredefinedNotes,
  VehicleWidget,
} from '@dms/shared';

import {isNotNilOrEmpty, parseDate, getApiDateString, suffixTestId, isNilOrZero} from 'shared/web';

import {useTestDriveMileageSubmit} from './hooks/useTestDriveMileageSubmit';
import {TestDriveDialogSchema, TestDriveDialogSchemaType} from './TestDriveDialogSchema';
import {TestDriveSelectVehicleDialog} from './TestDriveSelectVehicleDialog';
import {TestDriveStateSwitcher} from './TestDriveStateSwitcher';

interface TestDriveDialogProps {
  businessCaseId: string;
  vehicleId?: string;
  testDrive?: TestDriveResponseBody;
}

const sliderTicks = [
  {value: 0, label: ''},
  {value: 0.25, label: ''},
  {value: 0.5, label: ''},
  {value: 0.75, label: ''},
  {value: 1, label: ''},
];

const isReturnedAtInPast = (
  returnedAtDate: string | null | undefined,
  returnedAtTime: string | null | undefined
) => {
  if (!returnedAtDate || !returnedAtTime) {
    return false;
  }

  const parsedDate = parseDate(returnedAtDate);
  const parsedTime = getHoursAndMinutesFromTime(returnedAtTime);

  if (!parsedTime || !parsedDate) {
    return false;
  }

  const {hour, minute} = parsedTime;
  parsedDate.setHours(Number(hour));
  parsedDate.setMinutes(Number(minute));

  const currentDateTime = new Date();

  return isBefore(parsedDate, currentDateTime);
};

export function TestDriveDialog(props: TestDriveDialogProps) {
  const formatTime = useDateTimeFormatter();
  const [deleteTestDrive] = useDeleteTestDriveMutation();
  const [selectedVehicleId, setSelectedVehicleId] = useState(props.vehicleId ?? null);

  const {data: currentUser} = useGetCurrentUserInfoQuery();
  const {data: users, isLoading: areUsersLoading} = useGetUsersQuery();
  const {data: userData} = useGetUserQuery(
    {id: props.testDrive?.createdBy ?? ''},
    {skip: isNilOrEmpty(props.testDrive?.createdBy)}
  );

  const {data: vehicleData} = useGetVehicleV2Query(
    {vehicleId: selectedVehicleId ?? ''},
    {skip: isNilOrEmpty(selectedVehicleId)}
  );

  const {data: vehicleMileage} = useGetVehicleMileageQuery(
    {vehicleId: selectedVehicleId ?? ''},
    {skip: isNilOrEmpty(selectedVehicleId)}
  );

  const {handleSubmit} = useTestDriveMileageSubmit({
    businessCaseId: props.businessCaseId,
    selectedVehicleId,
    testDrive: props.testDrive,
  });

  const usersOptions = useMemo(
    () => mapUsersToSelectOptions(moveToHeadById(currentUser?.id ?? '', users ?? [])),
    [users, currentUser]
  );

  const defaultValues = {
    state: props.testDrive?.state ?? ('planned' as const),
    userId: props.testDrive?.userId ?? currentUser?.id ?? '',
    fuelBefore: (props.testDrive?.fuelBefore ?? 0) / 100,
    fuelAfter: (props.testDrive?.fuelAfter ?? 0) / 100,
    mileageBefore: props.testDrive?.mileageBefore ?? vehicleMileage?.mileage ?? 0,
    mileageAfter: props.testDrive?.mileageAfter ?? vehicleMileage?.mileage ?? 0,
    note: props.testDrive?.note ?? '',
    internalNote: props.testDrive?.internalNote ?? '',
    customerFeedback: props.testDrive?.customerFeedback ?? '',
    vehicleId: selectedVehicleId ?? null,
    scheduledAtDate: getApiDateString(
      props.testDrive?.scheduledAt
        ? (parseDate(props.testDrive.scheduledAt) ?? new Date())
        : new Date()
    ),
    scheduledAtTime: formatTime(
      'timeShort',
      props.testDrive?.scheduledAt
        ? (parseDate(props.testDrive.scheduledAt) ?? new Date())
        : new Date()
    ),
    returnedAtDate: props.testDrive?.returnedAt
      ? getApiDateString(parseDate(props.testDrive.returnedAt) ?? new Date())
      : null,
    returnedAtTime: props.testDrive?.returnedAt
      ? formatTime('timeShort', parseDate(props.testDrive.returnedAt) ?? new Date())
      : null,
  };

  const handleSelectVehicleCallback = (
    data: {vehicleId: string; start: Date; end: Date},
    formApi: UseFormReturn<TestDriveDialogSchemaType>
  ) => {
    setSelectedVehicleId(data.vehicleId);
    formApi.setValue('vehicleId', data.vehicleId);
    formApi.setValue('scheduledAtDate', data.start.toISOString().split('T')[0]);
    formApi.setValue('scheduledAtTime', data.start.toTimeString().split(' ')[0].substring(0, 5));
    formApi.setValue('returnedAtDate', data.end.toISOString().split('T')[0]);
    formApi.setValue('returnedAtTime', data.end.toTimeString().split(' ')[0].substring(0, 5));
    closeDialog('TEST_DRIVE_SELECT_VEHICLE_DIALOG');
  };

  const handleSelectVehicle = (formApi: UseFormReturn<TestDriveDialogSchemaType>) => {
    openDialog(
      <TestDriveSelectVehicleDialog
        onTestDriveSelect={(data) => handleSelectVehicleCallback(data, formApi)}
      />,
      {
        title: i18n.t('entity.testDrive.labels.selectVehicle'),
        size: 'large',
        id: 'TEST_DRIVE_SELECT_VEHICLE_DIALOG',
      }
    );
  };

  const handleOpenCalendar = (formApi: UseFormReturn<TestDriveDialogSchemaType>) => {
    openDialog(
      <TestDriveSelectVehicleDialog
        onTestDriveSelect={(data) => handleSelectVehicleCallback(data, formApi)}
        defaultVehicle={vehicleData as GetVehicleApiResponse}
        defaultActiveTab="calendar"
      />,

      {
        title: i18n.t('entity.testDrive.labels.selectVehicle'),
        size: 'large',
        id: 'TEST_DRIVE_SELECT_VEHICLE_DIALOG',
      }
    );
  };

  const handleDeleteTestDrive = () => {
    openConfirmDialog({
      onConfirm: async () =>
        await deleteTestDrive({
          testDriveId: props.testDrive?.id ?? '',
          businessCaseId: props.businessCaseId,
        })
          .unwrap()
          .then(() => {
            closeDialog('TEST_DRIVE_DIALOG');
          })
          .catch(handleApiError),
      text: i18n.t('entity.testDrive.labels.deleteTestDriveConfirmation'),
    });
  };

  return (
    <Form<TestDriveDialogSchemaType>
      experimentalZodSchema={TestDriveDialogSchema}
      onSubmit={handleSubmit}
      defaultValues={defaultValues}
    >
      {(control, formApi) => (
        <>
          <Box overflowX="hidden">
            <HStack spacing={4}>
              <VStack grow={2} spacing={4} maxWidth="80%">
                <Box>
                  <HStack width="100%" justify="space-between">
                    <Heading size={4}>
                      {i18n.t('entity.testDrive.labels.vehicleSectionHeader')}
                    </Heading>
                    <Button
                      title={i18n.t('entity.testDrive.labels.changeVehicle')}
                      size="small"
                      leftIcon="action/cached"
                      onClick={() => handleSelectVehicle(formApi)}
                      variant="link"
                    />
                  </HStack>
                  <Box paddingVertical={4}>
                    <Show when={isNilOrEmpty(vehicleData)}>
                      <EmptyStatus
                        variant="vehicle"
                        headline={i18n.t('entity.testDrive.labels.emptyVehicle')}
                        action={{
                          title: i18n.t('entity.testDrive.labels.selectVehicle'),
                          variant: 'primary',
                          leftIcon: 'content/add_circle',
                          onClick: () => handleSelectVehicle(formApi),
                        }}
                      />
                    </Show>
                    <Show when={isNotNilOrEmpty(vehicleData)}>
                      <VehicleWidget
                        vehicle={vehicleData!}
                        data-testid={suffixTestId('vehicleWidget', {
                          'data-testid': 'testDriveDialog',
                        })}
                      />
                    </Show>
                  </Box>
                </Box>

                <VStack spacing={4}>
                  <HStack width="100%" justify="space-between">
                    <Heading size={4}>
                      {i18n.t('entity.testDrive.labels.handoverSectionHeader')}
                    </Heading>
                    <Button
                      title={i18n.t('entity.testDrive.labels.openCalendar')}
                      size="small"
                      leftIcon="action/today"
                      onClick={() => handleOpenCalendar(formApi)}
                      variant="link"
                    />
                  </HStack>
                  <Grid columns={4} verticalSpacing={2}>
                    <FormField
                      control={control}
                      type="apiDate"
                      name="scheduledAtDate"
                      isRequired
                      label={i18n.t('entity.testDrive.labels.testDriveDate')}
                    />
                    <FormField
                      control={control}
                      type="time"
                      name="scheduledAtTime"
                      isRequired
                      label={i18n.t('entity.testDrive.labels.testDriveTime')}
                      minTime={
                        formApi.watch('scheduledAtDate') === getApiDateString(startOfToday())
                          ? (formatTime('timeShort', new Date()) as TimeType)
                          : undefined
                      }
                    />

                    <MileageInput
                      control={control}
                      formApi={formApi}
                      name="mileageBefore"
                      label={i18n.t('entity.testDrive.labels.mileageAtHandover')}
                      data-testid="testDriveDialog-mileageBefore"
                      vehicleId={selectedVehicleId}
                      shouldShowWarning={isNilOrZero(props.testDrive?.mileageBefore)}
                    />

                    <FormField
                      label={i18n.t('entity.testDrive.labels.fuelTank')}
                      name="fuelBefore"
                      type="slider"
                      control={control}
                      halfTicks={false}
                      max={1}
                      min={0}
                      step={0.25}
                      ticks={sliderTicks}
                    />
                    <FormField
                      control={control}
                      type="choice"
                      name="userId"
                      label={i18n.t('entity.testDrive.labels.driver')}
                      options={usersOptions}
                      isLoading={areUsersLoading}
                    />
                  </Grid>

                  <Separator spacing={0} />
                  <VStack>
                    <Box paddingBottom={4}>
                      <VStack spacing={1}>
                        <Heading size={4}>
                          {i18n.t('entity.testDrive.labels.returnSectionHeader')}
                        </Heading>
                        <Text size="xSmall" color="tertiary">
                          {i18n.t('entity.testDrive.labels.returnSectionSubtitle')}
                        </Text>
                      </VStack>
                    </Box>
                    <HStack spacing={4}>
                      <Box flex={1}>
                        <FormField
                          control={control}
                          type="apiDate"
                          name="returnedAtDate"
                          minDate={parseDate(formApi.watch('scheduledAtDate')) ?? startOfToday()}
                          label={i18n.t('entity.testDrive.labels.returnDate')}
                          onChange={(value) => {
                            if (value) {
                              if (isReturnedAtInPast(value, formApi.watch('returnedAtTime'))) {
                                formApi.setValue('state', 'completed');
                              } else {
                                formApi.setValue('state', 'planned');
                              }
                            }
                          }}
                        />
                      </Box>
                      <Box flex={1}>
                        <FormField
                          control={control}
                          type="time"
                          name="returnedAtTime"
                          minTime={
                            formApi.watch('returnedAtDate') === formApi.watch('scheduledAtDate')
                              ? (formApi.watch('scheduledAtTime') as TimeType)
                              : undefined
                          }
                          label={i18n.t('entity.testDrive.labels.returnTime')}
                          onChange={(value) => {
                            if (value) {
                              if (isReturnedAtInPast(formApi.watch('returnedAtDate'), value)) {
                                formApi.setValue('state', 'completed');
                              } else {
                                formApi.setValue('state', 'planned');
                              }
                            }
                          }}
                        />
                      </Box>
                      <Box flex={1}>
                        <MileageInput
                          control={control}
                          formApi={formApi}
                          name="mileageAfter"
                          label={i18n.t('entity.testDrive.labels.mileageAtReturn')}
                          data-testid="testDriveDialog-mileageAfter"
                          vehicleId={selectedVehicleId}
                          shouldShowWarning={isNilOrZero(props.testDrive?.mileageAfter)}
                          shouldShowDelta={isNilOrZero(props.testDrive?.mileageAfter)}
                        />
                      </Box>

                      <Box flex={1}>
                        <FormField
                          label={i18n.t('entity.testDrive.labels.fuelTank')}
                          name="fuelAfter"
                          type="slider"
                          halfTicks={false}
                          control={control}
                          max={1}
                          min={0}
                          step={0.25}
                          ticks={sliderTicks}
                        />
                      </Box>
                    </HStack>
                    <Separator />
                    <Box flex={1}>
                      <VStack spacing={1}>
                        <HStack justify="space-between" align="flex-end">
                          <Label>{i18n.t('entity.testDrive.labels.testDriveNote')}</Label>
                          <PredefinedNotes
                            note={formApi.watch('note') ?? null}
                            onPrefill={(note) => formApi.setValue('note', note)}
                            resource="TEST_DRIVE"
                            context="test_drive_note"
                            isLinkVariant
                            data-testid={suffixTestId('noteNotes', {
                              'data-testid': 'testDriveDialog',
                            })}
                          />
                        </HStack>
                        <FormField name="note" minRows={1} control={control} type="textarea" />
                      </VStack>
                    </Box>
                  </VStack>
                </VStack>
              </VStack>

              <Box
                flexGrow={2}
                width="20%"
                backgroundColor="palettes.neutral.20.100"
                borderRadius="medium"
                padding={4}
              >
                <VStack spacing={4}>
                  <HStack>
                    <TestDriveStateSwitcher name="state" control={control} />
                  </HStack>

                  <Show when={isNotNilOrEmpty(props.testDrive?.id)}>
                    <HStack width="100%" justify="space-between">
                      <Box flex={1}>
                        <Label>{i18n.t('entity.testDrive.labels.createdAt')}</Label>
                        <Text size="small">
                          {props.testDrive?.createdAt
                            ? formatTime('dateTimeShort', parseDate(props.testDrive.createdAt))
                            : ''}
                        </Text>
                      </Box>

                      <Box flex={1}>
                        <Label>{i18n.t('entity.testDrive.labels.createdBy')}</Label>
                        <Text size="small">{getNaturalPersonFullName(userData)}</Text>
                      </Box>
                    </HStack>
                  </Show>

                  <VStack spacing={1}>
                    <HStack justify="space-between" align="center">
                      <Label>{i18n.t('entity.testDrive.labels.internalNote')}</Label>
                      <PredefinedNotes
                        note={formApi.watch('internalNote') ?? null}
                        onPrefill={(note) => formApi.setValue('internalNote', note)}
                        resource="TEST_DRIVE"
                        context="test_drive_internal_note"
                        isLinkVariant
                        data-testid={suffixTestId('internalNoteNotes', {
                          'data-testid': 'testDriveDialog',
                        })}
                      />
                    </HStack>
                    <FormField name="internalNote" minRows={1} control={control} type="textarea" />
                  </VStack>

                  <VStack spacing={1}>
                    <HStack justify="space-between" align="center">
                      <Label>{i18n.t('entity.testDrive.labels.customerFeedback')}</Label>
                      <PredefinedNotes
                        note={formApi.watch('customerFeedback') ?? null}
                        onPrefill={(note) => formApi.setValue('customerFeedback', note)}
                        resource="TEST_DRIVE"
                        context="test_drive_customer_feedback"
                        isLinkVariant
                        data-testid={suffixTestId('customerFeedbackNotes', {
                          'data-testid': 'testDriveDialog',
                        })}
                      />
                    </HStack>
                    <FormField
                      name="customerFeedback"
                      minRows={1}
                      control={control}
                      type="textarea"
                    />
                  </VStack>

                  <Show when={isNotNilOrEmpty(props.testDrive?.id)}>
                    <Box flex={1}>
                      <Button
                        leftIcon="action/delete"
                        title={i18n.t('entity.testDrive.labels.delete')}
                        variant="dangerLink"
                        onClick={handleDeleteTestDrive}
                      />
                    </Box>
                  </Show>
                </VStack>
              </Box>
            </HStack>
          </Box>
          <Space vertical={4} />
          <DialogFooter>
            <ButtonGroup align="right">
              <Button
                variant="secondary"
                onClick={closeCurrentDialog}
                title={i18n.t('general.actions.discard')}
              />
              <FormButton
                variant="primary"
                isLoading={false}
                type="submit"
                control={control}
                title={i18n.t('general.actions.save')}
              />
            </ButtonGroup>
          </DialogFooter>
        </>
      )}
    </Form>
  );
}
